/**
 * Whether a food, or a whole meal, was ticked off — read-only.
 *
 * Not a `Checkbox`. A checkbox invites a click, and nothing on this screen is the trainer's to
 * change: the log belongs to the trainee. So this is a glyph that happens to look like one.
 *
 * Three states, because a meal can be half done and that is the most interesting state a meal has:
 * `all` a filled check, `part` a dash, `none` an empty box. It mirrors the tri-state mark on the
 * trainee's own meal card so the two screens read the same way.
 *
 * Carries `role="img"` and a Hebrew `aria-label` so a test can assert the STATE rather than the
 * colour, and so a screen reader gets the fact instead of a decorative box.
 */

import Box from "@mui/material/Box";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { v2 } from "../ui/v2Tokens";
import type { EatenState } from "../model/dayLogV2Model";

const LABEL: Record<EatenState, string> = {
  all: "נאכל",
  part: "נאכל חלקית",
  none: "לא סומן כנאכל",
};

export const EatenMark = ({ state, size = 18 }: { state: EatenState; size?: number }) => {
  const filled = state !== "none";
  return (
    <Box
      role="img"
      aria-label={LABEL[state]}
      data-testid={`eaten-mark-${state}`}
      sx={{
        flex: "0 0 auto",
        width: size,
        height: size,
        borderRadius: "5px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1.5px solid ${filled ? v2.brand : v2.line}`,
        backgroundColor: filled ? v2.brand : "transparent",
        color: "#FFFFFF",
      }}
    >
      {state === "all" && <CheckRoundedIcon sx={{ fontSize: size - 4 }} />}
      {state === "part" && <RemoveRoundedIcon sx={{ fontSize: size - 4 }} />}
    </Box>
  );
};
